import { useNavigate, useParams } from '@tanstack/react-router';
import { useState } from 'react';
import { Header } from '@/app/layout/Header';
import { Main } from '@/app/layout/Main';
import { Button, Modal } from '@devhop/ui';
import { ArrowLeft, FileText, HelpCircle } from 'lucide-react';
import { useCompanyPolicyDetailQuery } from '../api/queries';
import { usePolicyVersionsQuery } from '../api/queries';
import {
    PolicyVersionsTour,
    usePolicyVersionsTour,
} from '../tour/PolicyVersionsTour';
import {
    createSortableColumn,
    DataTable,
    type TanstackReactTable,
} from '@devhop/ui';
import type { CompanyPolicyVersionListItem } from '@/modules/hr/company-policy/domain/types';

function formatDate(s: string) {
    try {
        return new Date(s).toLocaleDateString('lo-LA', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    } catch {
        return s;
    }
}

export function PolicyVersionsPage() {
    const { id } = useParams({ strict: false });
    const nav = useNavigate();
    const [viewingVersion, setViewingVersion] =
        useState<CompanyPolicyVersionListItem | null>(null);
    const detailQuery = useCompanyPolicyDetailQuery(id);
    const versionsQuery = usePolicyVersionsQuery(id, { offset: 0, limit: 50 });
    const policy = detailQuery.data;
    const versions = versionsQuery.data?.data ?? [];
    const tour = usePolicyVersionsTour();

    const columns: TanstackReactTable.ColumnDef<CompanyPolicyVersionListItem>[] =
        [
            createSortableColumn<CompanyPolicyVersionListItem>(
                'version',
                'ສະບັບ',
                {
                    size: 80,
                },
            ),
            createSortableColumn<CompanyPolicyVersionListItem>(
                'effectiveFrom',
                'ມີຜົນແຕ່',
                {
                    size: 120,
                    cell: ({ row }) => formatDate(row.original.effectiveFrom),
                },
            ),
            createSortableColumn<CompanyPolicyVersionListItem>(
                'effectiveTo',
                'ສິ້ນສຸດ',
                {
                    size: 120,
                    cell: ({ row }) =>
                        row.original.effectiveTo
                            ? formatDate(row.original.effectiveTo)
                            : '—',
                },
            ),
            createSortableColumn<CompanyPolicyVersionListItem>(
                'createdAt',
                'ວັນທີສ້າງ',
                {
                    size: 120,
                    cell: ({ row }) => formatDate(row.original.createdAt),
                },
            ),
            {
                id: 'actions',
                header: '',
                size: 120,
                cell: ({ row }) => (
                    <Button
                        variant="outline"
                        size="sm"
                        className="gap-1.5"
                        onClick={() => setViewingVersion(row.original)}
                        aria-label="ເບິ່ງເນື້ອໃນ"
                    >
                        <FileText className="h-4 w-4" />
                        ເບິ່ງເນື້ອໃນ
                    </Button>
                ),
            },
        ];

    const handleBack = () =>
        nav({ to: '/app/hr/company-policy/$id', params: { id: id ?? '' } });

    return (
        <>
            <Header />
            <Main>
                <PolicyVersionsTour
                    run={tour.run}
                    onCallback={tour.handleJoyrideCallback}
                />
                <div
                    className="mb-6 flex items-center gap-2"
                    data-tourid="policy-versions-back"
                >
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleBack}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        ກັບຄືນ
                    </Button>
                    <Button
                        variant="ghost"
                        size="icon"
                        className="size-7 rounded-full"
                        onClick={tour.startTour}
                        title="ເລີ່ມທົດສອບການນຳທາງ"
                        aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                    >
                        <HelpCircle className="size-5" />
                    </Button>
                </div>

                <div className="mx-auto max-w-4xl">
                    <h1 className="mb-2 font-semibold text-2xl">
                        ປະຫວັດສະບັບນະໂຍບາຍ
                    </h1>
                    {policy && (
                        <p className="mb-6 text-muted-foreground text-sm">
                            {policy.title}
                        </p>
                    )}

                    <div data-tourid="policy-versions-table">
                        <DataTable
                            columns={columns}
                            data={versions}
                            isLoading={versionsQuery.isLoading}
                            offset={0}
                            limit={versions.length}
                            totalCount={versions.length}
                            onPaginationChange={() => {}}
                        />
                    </div>

                    <Modal
                        open={!!viewingVersion}
                        onOpenChange={(open) =>
                            !open && setViewingVersion(null)
                        }
                        title={
                            viewingVersion
                                ? `ເນື້ອໃນສະບັບ ${viewingVersion.version}`
                                : ''
                        }
                        description={
                            viewingVersion
                                ? `ມີຜົນຕັ້ງແຕ່ ${formatDate(viewingVersion.effectiveFrom)}${
                                      viewingVersion.effectiveTo
                                          ? ` — ສິ້ນສຸດ ${formatDate(viewingVersion.effectiveTo)}`
                                          : ''
                                  }`
                                : ''
                        }
                        size="lg"
                    >
                        {viewingVersion && (
                            <div className="space-y-3">
                                {viewingVersion.fileKey &&
                                    !viewingVersion.content && (
                                        <p className="text-muted-foreground text-sm">
                                            ເນື້ອໃນເກັບໃນໄຟລ໌ (file key:{' '}
                                            {viewingVersion.fileKey})
                                        </p>
                                    )}
                                {viewingVersion.content ? (
                                    <pre className="max-h-[60vh] overflow-auto rounded-md border bg-muted/30 p-4 text-sm font-mono whitespace-pre-wrap">
                                        {viewingVersion.content}
                                    </pre>
                                ) : !viewingVersion.fileKey ? (
                                    <p className="text-muted-foreground text-sm">
                                        ບໍ່ມີເນື້ອໃນ
                                    </p>
                                ) : null}
                            </div>
                        )}
                    </Modal>
                </div>
            </Main>
        </>
    );
}
